import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  QrCode, 
  Plus, 
  Copy, 
  Check, 
  X, 
  Loader2, 
  RefreshCw,
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Image,
  Upload,
  Trash2,
  Eye,
  Bell,
  BellRing,
  PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = __BACKEND_URL__;

const STATUS_COLORS = {
  'ATIVA': 'bg-blue-100 text-blue-800',
  'CONCLUIDA': 'bg-green-100 text-green-800',
  'PAGO': 'bg-green-100 text-green-800',
  'CANCELADA': 'bg-red-100 text-red-800',
  'REMOVIDA_PELO_USUARIO_RECEBEDOR': 'bg-red-100 text-red-800',
  'ERRO': 'bg-amber-100 text-amber-800',
  'EXPIRADA': 'bg-slate-100 text-slate-800'
};

const STATUS_LABELS = {
  'ATIVA': 'Ativa',
  'CONCLUIDA': 'Pago',
  'PAGO': 'Pago',
  'CANCELADA': 'Cancelada',
  'REMOVIDA_PELO_USUARIO_RECEBEDOR': 'Cancelada',
  'ERRO': 'Erro',
  'EXPIRADA': 'Expirada'
};

const STATUS_ICONS = {
  'ATIVA': Clock,
  'CONCLUIDA': CheckCircle,
  'PAGO': CheckCircle,
  'CANCELADA': XCircle,
  'REMOVIDA_PELO_USUARIO_RECEBEDOR': XCircle,
  'ERRO': AlertCircle,
  'EXPIRADA': Clock
};

export const CobrancasPage = () => {
  const [activeTab, setActiveTab] = useState('lista');
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bbStatus, setBbStatus] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Polling para verificar pagamentos
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const pollingIntervalRef = useRef(null);
  const [lastPollResult, setLastPollResult] = useState(null);
  
  // New cobrança form
  const [formData, setFormData] = useState({
    numero_pedido: '',
    valor: '',
    descricao: '',
    nome_pagador: '',
    cpf_cnpj_pagador: ''
  });
  const [creating, setCreating] = useState(false);
  
  // QR Code modal
  const [selectedCobranca, setSelectedCobranca] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  
  // Image viewer modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Check BB status
  const checkBBStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cobrancas/status`);
      setBbStatus(response.data);
    } catch (error) {
      console.error('Error checking BB status:', error);
      setBbStatus({ configured: false, authenticated: false });
    }
  }, []);

  // Load cobrancas
  const loadCobrancas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/cobrancas?limit=100`);
      setCobrancas(response.data.cobrancas || []);
    } catch (error) {
      console.error('Error loading cobrancas:', error);
      toast.error('Erro ao carregar cobranças');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cobrancas/stats/summary`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    checkBBStatus();
    loadCobrancas();
    loadStats();
  }, [checkBBStatus, loadCobrancas, loadStats]);

  // Polling para verificar pagamentos em tempo real
  const pollPaymentStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cobrancas/poll/ativas`);
      setLastPollResult(response.data);
      
      if (response.data.has_updates && response.data.updated?.length > 0) {
        // Pagamento confirmado!
        response.data.updated.forEach(pmt => {
          toast.success(
            <div className="flex flex-col">
              <span className="font-bold">Pagamento Confirmado!</span>
              <span>Pedido #{pmt.numero_pedido}</span>
              <span className="text-emerald-600 font-bold">{formatCurrency(pmt.valor)}</span>
            </div>,
            { duration: 10000 }
          );
        });
        
        // Recarregar lista e stats
        loadCobrancas();
        loadStats();
        
        // Se o modal está aberto, atualizar a cobrança selecionada
        if (selectedCobranca) {
          const updated = response.data.updated.find(u => u.txid === selectedCobranca.txid);
          if (updated) {
            setPaymentConfirmed(true);
            setSelectedCobranca(prev => ({ ...prev, status: 'CONCLUIDA', pago_em: updated.pago_em }));
          }
        }
      }
    } catch (error) {
      console.error('Erro no polling:', error);
    }
  }, [loadCobrancas, loadStats, selectedCobranca]);

  // Iniciar/parar polling
  useEffect(() => {
    if (pollingEnabled) {
      // Poll a cada 5 segundos
      pollingIntervalRef.current = setInterval(pollPaymentStatus, 5000);
      // Poll imediatamente ao iniciar
      pollPaymentStatus();
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [pollingEnabled, pollPaymentStatus]);

  // Verificar pagamento de uma cobrança específica
  const checkPaymentStatus = async (txid) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/api/cobrancas/${txid}/refresh`);
      
      if (response.data.pago) {
        setPaymentConfirmed(true);
        setSelectedCobranca(prev => ({ 
          ...prev, 
          status: 'CONCLUIDA',
          pago_em: response.data.pix_info?.horario,
          valor_pago: response.data.pix_info?.valor
        }));
        toast.success('Pagamento confirmado!');
        loadCobrancas();
        loadStats();
      } else {
        toast.info('Aguardando pagamento...');
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar pagamento:', error);
      toast.error('Erro ao verificar status');
    } finally {
      setCheckingPayment(false);
    }
  };

  // Create cobrança
  const handleCreateCobranca = async (e) => {
    e.preventDefault();
    
    if (!formData.numero_pedido || !formData.valor) {
      toast.error('Preencha o número do pedido e valor');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/api/cobrancas`, {
        ...formData,
        valor: parseFloat(formData.valor)
      });
      
      toast.success('Cobrança PIX criada com sucesso!');
      
      // Show QR code
      setSelectedCobranca(response.data.cobranca);
      setShowQrModal(true);
      
      // Reset form
      setFormData({
        numero_pedido: '',
        valor: '',
        descricao: '',
        nome_pagador: '',
        cpf_cnpj_pagador: ''
      });
      
      // Reload list
      loadCobrancas();
      loadStats();
      setActiveTab('lista');
      
    } catch (error) {
      console.error('Error creating cobrança:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar cobrança');
    } finally {
      setCreating(false);
    }
  };

  // Cancel cobrança
  const handleCancelCobranca = async (txid) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta cobrança?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/cobrancas/${txid}`);
      toast.success('Cobrança cancelada');
      loadCobrancas();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar cobrança');
    }
  };

  // Copy PIX code
  const handleCopyPix = async (pixCode) => {
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erro ao copiar código');
    }
  };

  // Upload image
  const handleUploadImage = async (cobrancaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/api/cobrancas/${cobrancaId}/image`, formData);
      toast.success('Imagem salva!');
      loadCobrancas();
    } catch (error) {
      toast.error('Erro ao salvar imagem');
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="cobrancas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cobranças PIX</h1>
          <p className="text-slate-500">Gere e gerencie cobranças PIX via Banco do Brasil</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* BB Status Badge */}
          {bbStatus && (
            <div className="flex items-center gap-2">
              {bbStatus.environment === 'sandbox' && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  Sandbox
                </Badge>
              )}
              <Badge 
                variant={bbStatus.authenticated ? "default" : "destructive"}
                className="flex items-center gap-1"
              >
                {bbStatus.authenticated ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    BB Conectado
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3" />
                    BB Desconectado
                  </>
                )}
              </Badge>
            </div>
          )}
          
          {/* Polling Status */}
          <Button
            onClick={() => setPollingEnabled(!pollingEnabled)}
            variant={pollingEnabled ? "default" : "outline"}
            size="sm"
            className={pollingEnabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            title={pollingEnabled ? "Monitoramento ativo - clique para pausar" : "Monitoramento pausado - clique para ativar"}
          >
            {pollingEnabled ? (
              <>
                <BellRing className="w-4 h-4 mr-2 animate-pulse" />
                Monitorando
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Pausado
              </>
            )}
          </Button>
          
          <Button onClick={() => { loadCobrancas(); loadStats(); }} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-600">Ativas</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.ativas}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-600">Pagas</p>
                  <p className="text-2xl font-bold text-green-900">{stats.concluidas}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-emerald-600">Recebido</p>
                  <p className="text-xl font-bold text-emerald-900">{formatCurrency(stats.valor_recebido)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="lista" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Cobranças
          </TabsTrigger>
          <TabsTrigger value="nova" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nova Cobrança
          </TabsTrigger>
        </TabsList>

        {/* List Tab */}
        <TabsContent value="lista" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lista de Cobranças</CardTitle>
              <CardDescription>Todas as cobranças PIX geradas</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : cobrancas.length === 0 ? (
                <div className="text-center py-12">
                  <QrCode className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nenhuma cobrança encontrada</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setActiveTab('nova')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeira cobrança
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cobrancas.map((cob) => {
                    const StatusIcon = STATUS_ICONS[cob.status] || Clock;
                    return (
                      <div 
                        key={cob.txid}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          {/* Image thumbnail or placeholder */}
                          <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {cob.imagem_pedido ? (
                              <img 
                                src={cob.imagem_pedido} 
                                alt="Pedido" 
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => {
                                  setSelectedImage(cob.imagem_pedido);
                                  setShowImageModal(true);
                                }}
                              />
                            ) : (
                              <label className="cursor-pointer w-full h-full flex items-center justify-center hover:bg-slate-300 transition-colors">
                                <input 
                                  type="file" 
                                  accept="image/*" 
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleUploadImage(cob.id, e.target.files[0]);
                                    }
                                  }}
                                />
                                <Upload className="w-5 h-5 text-slate-400" />
                              </label>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-900">Pedido #{cob.numero_pedido}</span>
                              <Badge className={STATUS_COLORS[cob.status] || 'bg-slate-100'}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {STATUS_LABELS[cob.status] || cob.status}
                              </Badge>
                            </div>
                            <p className="text-lg font-bold text-emerald-600">{formatCurrency(cob.valor)}</p>
                            <p className="text-xs text-slate-500">
                              {formatDate(cob.created_at)}
                              {cob.nome_pagador && ` • ${cob.nome_pagador}`}
                            </p>
                            {cob.descricao && (
                              <p className="text-sm text-slate-600 mt-1 truncate">{cob.descricao}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-3 md:mt-0">
                          {cob.status === 'ATIVA' && cob.pix_copia_cola && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCopyPix(cob.pix_copia_cola)}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copiar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedCobranca(cob);
                                  setShowQrModal(true);
                                }}
                              >
                                <QrCode className="w-4 h-4 mr-1" />
                                QR Code
                              </Button>
                            </>
                          )}
                          {cob.status === 'ATIVA' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleCancelCobranca(cob.txid)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* New Cobrança Tab */}
        <TabsContent value="nova" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <QrCode className="w-5 h-5 text-blue-600" />
                Nova Cobrança PIX
              </CardTitle>
              <CardDescription>Preencha os dados para gerar uma cobrança PIX</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateCobranca} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Número do Pedido */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Número do Pedido *
                    </label>
                    <Input
                      placeholder="Ex: 12345"
                      value={formData.numero_pedido}
                      onChange={(e) => setFormData({ ...formData, numero_pedido: e.target.value })}
                      required
                    />
                  </div>
                  
                  {/* Valor */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Valor (R$) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0,00"
                      value={formData.valor}
                      onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                      required
                    />
                  </div>
                  
                  {/* Descrição */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Descrição / Observações
                    </label>
                    <Input
                      placeholder="Ex: Compra de mercadorias"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                  </div>
                  
                  {/* Nome do Pagador */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Nome do Pagador
                    </label>
                    <Input
                      placeholder="Nome do cliente/fornecedor"
                      value={formData.nome_pagador}
                      onChange={(e) => setFormData({ ...formData, nome_pagador: e.target.value })}
                    />
                  </div>
                  
                  {/* CPF/CNPJ do Pagador */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      CPF/CNPJ do Pagador
                    </label>
                    <Input
                      placeholder="000.000.000-00"
                      value={formData.cpf_cnpj_pagador}
                      onChange={(e) => setFormData({ ...formData, cpf_cnpj_pagador: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({
                      numero_pedido: '',
                      valor: '',
                      descricao: '',
                      nome_pagador: '',
                      cpf_cnpj_pagador: ''
                    })}
                  >
                    Limpar
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={creating || !bbStatus?.authenticated}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gerando...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-4 h-4 mr-2" />
                        Gerar Cobrança PIX
                      </>
                    )}
                  </Button>
                </div>
                
                {!bbStatus?.authenticated && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Integração com Banco do Brasil não está ativa. Verifique as credenciais.
                    </p>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={(open) => {
        setShowQrModal(open);
        if (!open) {
          setPaymentConfirmed(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentConfirmed ? (
                <>
                  <PartyPopper className="w-5 h-5 text-emerald-600" />
                  Pagamento Confirmado!
                </>
              ) : (
                <>
                  <QrCode className="w-5 h-5 text-blue-600" />
                  Cobrança PIX
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedCobranca && `Pedido #${selectedCobranca.numero_pedido}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCobranca && (
            <div className="space-y-4">
              {/* Status de Pagamento Confirmado */}
              {paymentConfirmed || selectedCobranca.status === 'CONCLUIDA' ? (
                <div className="text-center py-6 bg-emerald-50 rounded-xl border-2 border-emerald-200">
                  <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-3" />
                  <p className="text-xl font-bold text-emerald-700">Pagamento Recebido!</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-2">
                    {formatCurrency(selectedCobranca.valor_pago || selectedCobranca.valor)}
                  </p>
                  {selectedCobranca.pago_em && (
                    <p className="text-sm text-emerald-600 mt-2">
                      {formatDate(selectedCobranca.pago_em)}
                    </p>
                  )}
                  <div className="mt-4 p-3 bg-emerald-100 rounded-lg mx-4">
                    <p className="text-sm font-medium text-emerald-800">
                      ✅ Pode liberar o pedido para o cliente!
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* QR Code Image */}
                  {selectedCobranca.qr_code_base64 && (
                    <div className="flex justify-center p-4 bg-white rounded-xl border relative">
                      <img 
                        src={`data:image/png;base64,${selectedCobranca.qr_code_base64}`}
                        alt="QR Code PIX"
                        className="w-48 h-48"
                      />
                      {/* Indicador de aguardando pagamento */}
                      <div className="absolute -top-2 -right-2">
                        <div className="relative">
                          <BellRing className="w-6 h-6 text-blue-500 animate-pulse" />
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-ping" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Valor */}
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-600">
                      {formatCurrency(selectedCobranca.valor)}
                    </p>
                    <p className="text-sm text-slate-500 mt-1 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Aguardando pagamento...
                    </p>
                  </div>
                  
                  {/* PIX Copia e Cola */}
                  {selectedCobranca.pix_copia_cola && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        PIX Copia e Cola
                      </label>
                      <div className="flex gap-2">
                        <Input 
                          value={selectedCobranca.pix_copia_cola}
                          readOnly
                          className="text-xs font-mono"
                        />
                        <Button
                          size="sm"
                          variant={copied ? "default" : "outline"}
                          onClick={() => handleCopyPix(selectedCobranca.pix_copia_cola)}
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Botão de verificar pagamento */}
                  <Button
                    onClick={() => checkPaymentStatus(selectedCobranca.txid)}
                    disabled={checkingPayment}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {checkingPayment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Verificar Pagamento
                      </>
                    )}
                  </Button>
                </>
              )}
              
              {/* Status Badge */}
              {!paymentConfirmed && selectedCobranca.status !== 'CONCLUIDA' && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Badge className={STATUS_COLORS[selectedCobranca.status] || 'bg-slate-100'}>
                    {STATUS_LABELS[selectedCobranca.status] || selectedCobranca.status}
                  </Badge>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQrModal(false)}>
              {paymentConfirmed || selectedCobranca?.status === 'CONCLUIDA' ? 'Concluir' : 'Fechar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Imagem do Pedido</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex justify-center">
              <img 
                src={selectedImage} 
                alt="Pedido" 
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CobrancasPage;
